import React from "react";
import { Link } from "react-router-dom";

// Fonction utilitaire pour formater le lieu de consommation
const formatLieuConso = (lieu) => {
  const mapping = {
    on_place: "Sur place",
    emporter: "À emporter",
  };
  return mapping[lieu] || lieu;
};

const Purchases = ({ purchases, getLocalDate }) => {
  // Rendu du chargement
  const renderLoading = () => (
    <div className="space-y-4">
      <div className="flex justify-center items-center py-10">
        <div
          className="animate-spin rounded-full h-8 w-8 border-t-2 border-amber-500"
          aria-label="Chargement en cours"
        />
        <span className="ml-3 text-amber-600 font-medium">
          Chargement des achats...
        </span>
      </div>

      {/* Squelette version desktop */}
      <div className="hidden sm:block space-y-2">
        {[...Array(3)].map((_, index) => (
          <div
            key={`skeleton-desktop-${index}`}
            className="animate-pulse flex space-x-4 px-6 py-3"
          >
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-4 bg-amber-100 rounded w-1/4" />
            ))}
          </div>
        ))}
      </div>

      {/* Squelette version mobile */}
      <div className="block sm:hidden space-y-4">
        {[...Array(3)].map((_, index) => (
          <div
            key={`skeleton-mobile-${index}`}
            className="p-4 bg-amber-50 rounded-lg shadow-sm animate-pulse"
          >
            <div className="h-4 bg-amber-100 rounded w-3/4 mb-2" />
            <div className="h-4 bg-amber-100 rounded w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );

  // Rendu du tableau desktop
  const renderDesktopTable = () => (
    <div className="hidden sm:block overflow-x-auto animate-fade-in">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {[
              "N° Facture",
              "Date d'achat",
              "Prix Total",
              "Lieu de consommation",
              "Actions",
            ].map((header) => (
              <th
                key={header}
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {purchases.map((purchase) => (
            <tr
              key={purchase.id}
              className="hover:bg-amber-50 transition-all duration-150"
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-amber-600">
                {purchase.num_facture}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {getLocalDate(new Date(purchase.date_sortie))}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {purchase.prix_total?.toLocaleString("fr-FR", {
                  style: "currency",
                  currency: "XOF",
                })}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {formatLieuConso(purchase.lieu_conso)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <Link
                  to={`/sales/details/${purchase.id}`}
                  className="text-amber-600 hover:text-amber-800 transition-colors duration-200"
                >
                  Détails
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Rendu des cartes mobiles
  const renderMobileCards = () => (
    <div className="block sm:hidden space-y-4 animate-fade-in">
      {purchases.map((purchase) => (
        <div
          key={`mobile-${purchase.id}`}
          className="p-4 bg-gray-50 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:bg-amber-50"
        >
          <Link
            to={`/sales/details/${purchase.id}`}
            className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm"
          >
            <span className="font-semibold text-amber-600">N° Facture:</span>
            <span>{purchase.num_facture}</span>

            <span className="font-semibold text-amber-600">Date:</span>
            <span>{getLocalDate(new Date(purchase.date_sortie))}</span>

            <span className="font-semibold text-amber-600">Prix Total:</span>
            <span>
              {purchase.prix_total?.toLocaleString("fr-FR", {
                style: "currency",
                currency: "XOF",
              })}
            </span>

            <span className="font-semibold text-amber-600">Lieu:</span>
            <span>{formatLieuConso(purchase.lieu_conso)}</span>
          </Link>
        </div>
      ))}
    </div>
  );

  // Rendu lorsque aucune donnée n'est disponible
  const renderEmptyState = () => (
    <div className="py-10 text-center">
      <div className="flex flex-col items-center">
        <svg
          className="w-16 h-16 text-gray-300 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
        <p className="text-gray-500 text-lg">
          Aucun achat trouvé pour ce client
        </p>
        <p className="text-gray-400 text-sm mt-1">
          Les transactions apparaîtront ici une fois effectuées
        </p>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto p-6 bg-white shadow-md h-full rounded-xl transition-all duration-300 hover:shadow-lg">
      <div className="flex items-center border-b pb-4 mb-4">
        <div className="p-2 bg-amber-100 rounded-lg text-amber-600 mr-3">
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900">
          Historique des achats
        </h2>
      </div>

      {purchases === null ? (
        renderLoading()
      ) : purchases.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          {renderDesktopTable()}
          {renderMobileCards()}
        </>
      )}
    </div>
  );
};

export default Purchases;
